<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'
import ContactForm, { type ContactFormExpose } from '@/ui-kit/help/ContactForm.vue'

interface PageContext {
  links: {
    oferta: string
  }
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()
const asset = useStaticAsset()

const teacherModalOpen = ref(false)
const parentModalOpen = ref(false)
const childModalOpen = ref(false)
const contactForm = useTemplateRef<ContactFormExpose>('contactForm')
</script>

<template>
  <main v-cloak id="studyinjoy-page" class="contents studyinjoy-page">
    <header class="studyinjoy-page__header">
      <a href="/public">
        <img :height="isMobile ? 40 : 64" :src="asset('img/logo.svg')" />
      </a>

      <v-button href="/users/login?student_login=true" :mode="isMobile ? 'primary text' : 'primary large'">
        Войти на сайт
      </v-button>
    </header>

    <section class="studyinjoy-page__hero">
      <div class="studyinjoy-page__hero-inner">
        <img class="studyinjoy-page__hero-bg-1" :src="asset('img/study-in-joy/hero-bg-1.svg')" />
        <img class="studyinjoy-page__hero-bg-2" :src="asset('img/study-in-joy/hero-bg-2.svg')" />

        <div class="studyinjoy-page__hero-content">
          <h1 class="studyinjoy-page__hero-title">
            Cистема<br />
            <span class="orange-text">Валерии Мещеряковой</span>
          </h1>

          <p class="mt-2 studyinjoy-page__hero-body">
            Методика и&nbsp;учебные материалы для естественного обучения иностранным языкам. Обучайте детей иностранному
            языку так&nbsp;же, как они освоили родной.
          </p>

          <ul class="mt-5 studyinjoy-page__hero-actions">
            <v-button mode="xlarge warn" href="https://promo.mirgovorit.ru/basic_seminar"> Базовый семинар </v-button>

            <v-button mode="xlarge outlined" href="#learn-more"> Подробнее </v-button>
          </ul>
        </div>
      </div>

      <div class="studyinjoy-page__hero-stats">
        <img class="studyinjoy-page__hero-emblem" :src="asset('img/study-in-joy/hero-emblem.svg')" />
        <ul class="studyinjoy-page__hero-cards">
          <li class="studyinjoy-page__hero-card studyinjoy-page__hero-card_orange">
            <p>3 500 +</p>
            <p>обучившихся на семинаре</p>
          </li>
          <li class="studyinjoy-page__hero-card studyinjoy-page__hero-card_blue">
            <p>250 +</p>
            <p>школ и центров в России и СНГ</p>
          </li>
          <li class="studyinjoy-page__hero-card studyinjoy-page__hero-card_green">
            <p>Более 30 лет</p>
            <p>практического&nbsp;<br v-if="!isMobile" />опыта</p>
          </li>
        </ul>
      </div>
    </section>

    <section class="studyinjoy-page__learning">
      <img class="studyinjoy-page__learning-bg" :src="asset('img/study-in-joy/learning-bg.svg')" />
      <h2 class="studyinjoy-page__section-heading studyinjoy-page__learning-heading">Обучение детей без перевода</h2>

      <ul class="studyinjoy-page__learning-grid">
        <li class="studyinjoy-page__learning-info-card">
          <v-icon v-if="!isMobile" size="88">study-in-joy/learning-lamp-icon</v-icon>
          <div>
            <p>
              Мозг ребёнка до 8-10 лет устроен таким образом, что любой новый язык может как бы "впечатываться" в него,
              образуя что-то вроде нового языкового центра.
            </p>
            <p class="mt-2">
              Это значит, что ребёнок обучится иностранному языку так же, как он освоил родной: от понимания обращённой
              к нему речи, до умения говорить, функционально читать, писать на иностранном языке и анализировать его
              грамматику.
            </p>
          </div>
        </li>

        <li class="studyinjoy-page__learning-paragraph">
          На базе этой природной особенности построены принципы и подходы Системы Валерии Мещеряковой, позволяющие
          создать непринуждённую и, в то же время, эффективную учебную и развивающую среду для детей.
        </li>

        <li class="studyinjoy-page__learning-photo">
          <img :src="asset('img/study-in-joy/learning-img1.webp')" />
        </li>

        <li class="studyinjoy-page__learning-photo-big">
          <img v-if="isMobile" :src="asset('img/study-in-joy/learning-img2-mobile.webp')" />
          <img v-else :src="asset('img/study-in-joy/learning-img2-desktop.webp')" />
        </li>
      </ul>
    </section>

    <section id="learn-more" class="studyinjoy-page__pros">
      <h2 class="studyinjoy-page__section-heading studyinjoy-page__pros-heading">Преимущества методики</h2>

      <ul class="studyinjoy-page__pros-list">
        <li class="studyinjoy-page__pros-card">
          <div class="studyinjoy-page__pros-card-header">
            <v-icon :size="isMobile ? 24 : 56">study-in-joy/pros-globe-icon</v-icon>
            <p>Для учителей</p>
          </div>

          <div class="studyinjoy-page__pros-card-body">
            <p>
              Работа строится в системе, в которой уроки и материалы уже продуманы на год, три, пять лет вперёд, и вы
              точно будете знать, что не тратите время урока на лишнее или не убрали что-то очень важное для будущих
              результатов.
            </p>
            <p>
              Учителя активно общаются друг с другом. Поэтому у новых учителей всегда есть возможность посоветоваться и
              получить поддержку более опытных коллег.
            </p>
          </div>

          <v-link class="d-flex justify-space-between text-body-bold blue-text" @click="teacherModalOpen = true">
            Подробнее
            <v-icon>mdi-arrow-right</v-icon>
          </v-link>
        </li>

        <li class="studyinjoy-page__pros-card">
          <div class="studyinjoy-page__pros-card-header">
            <v-icon :size="isMobile ? 24 : 56">study-in-joy/pros-lamp-icon</v-icon>
            <p>Для родителей</p>
          </div>

          <div class="studyinjoy-page__pros-card-body">
            <p>
              Методика даёт возможность родителям минимально участвовать в обучении ребёнка. Загруженным родителям
              достаточно приучить ребёнка к ежедневному прослушиванию аудио курса и напомнить о выполнении заданий на
              контрольном листе 1 раз в 2 недели!
            </p>
          </div>

          <v-link class="d-flex justify-space-between text-body-bold green-text" @click="parentModalOpen = true">
            Подробнее
            <v-icon>mdi-arrow-right</v-icon>
          </v-link>
        </li>

        <li class="studyinjoy-page__pros-card">
          <div class="studyinjoy-page__pros-card-header">
            <v-icon :size="isMobile ? 24 : 56">study-in-joy/pros-star-icon</v-icon>
            <p>Для детей</p>
          </div>

          <div class="studyinjoy-page__pros-card-body">
            <p>
              Игровые уроки с большим количеством игрушек и картинок, подвижные игры и только положительные эмоции при
              общении на иностранном языке без страха и барьеров.
            </p>
          </div>

          <v-link class="d-flex justify-space-between text-body-bold orange-text" @click="childModalOpen = true">
            Подробнее
            <v-icon>mdi-arrow-right</v-icon>
          </v-link>
        </li>
      </ul>
    </section>

    <section class="studyinjoy-page__work">
      <img class="studyinjoy-page__work-bg" :src="asset('img/study-in-joy/work-bg.webp')" />
      <header class="studyinjoy-page__work-header">
        <h2>Вы сможете работать более эффективно</h2>
        <p>Мы предлагаем педагогам полный набор инструментов для проведения действительно результативных уроков</p>
      </header>

      <ul class="studyinjoy-page__work-grid">
        <li>
          <v-icon :size="isMobile ? 32 : 56">study-in-joy/work-icon-1</v-icon>
          <div class="studyinjoy-page__work-card-content">
            <h3>Программа на несколько лет</h3>
            <p>Система Валерии Мещеряковой состоит из 5 ступеней, каждая из которых рассчитана на учебный год.</p>
          </div>
        </li>
        <li>
          <v-icon :size="isMobile ? 32 : 56">study-in-joy/work-icon-2</v-icon>
          <div class="studyinjoy-page__work-card-content">
            <h3>Готовые планы и наглядность</h3>
            <p>
              Чтобы приготовится к уроку требуется только изучить готовый план, подготовить нужный наглядный материал из
              готового комплекта и игрушки, отмеченные в плане.
            </p>
          </div>
        </li>
        <li>
          <v-icon :size="isMobile ? 32 : 56">study-in-joy/work-icon-3</v-icon>
          <div class="studyinjoy-page__work-card-content">
            <h3>Поэтапная работа с материалом</h3>
            <p>
              Все этапы, от ввода до закрепления материала, уже зашиты в планы уроков. Вам не нужно придумывать, как
              объяснить детям грамматику или сложные правила чтения английского языка.
            </p>
          </div>
        </li>
        <li>
          <v-icon :size="isMobile ? 32 : 56">study-in-joy/work-icon-4</v-icon>
          <div class="studyinjoy-page__work-card-content">
            <h3>Алгоритмы закрепления материала</h3>
            <p>
              Дети не забывают пройденный материал, а с каждым следующим уроком активней ГОВОРЯТ на английском, и даже
              могут провести часть урока за вас, благодаря системности введения и отработки материала.
            </p>
          </div>
        </li>
        <li>
          <v-icon :size="isMobile ? 32 : 56">study-in-joy/work-icon-5</v-icon>
          <div class="studyinjoy-page__work-card-content">
            <h3>Простой и эффективный контроль</h3>
            <p>
              Раз в 2 недели проводятся так называемые "контрольные уроки", на которых учитель проверяет качество
              усвоения ребёнком материала.
            </p>
          </div>
        </li>
        <li>
          <v-icon :size="isMobile ? 32 : 56">study-in-joy/work-icon-6</v-icon>
          <div class="studyinjoy-page__work-card-content">
            <h3>Широкий пласт для творчества и развития</h3>
            <p>
              Программа закладывает прочный остов знаний и навыков у детей, сформировав который вы можете создавать
              дополнительные направления для своих клиентов: разговорные интенсивы, лагеря, клубы и т.д.
            </p>
          </div>
        </li>
      </ul>
    </section>

    <section class="studyinjoy-page__seminars">
      <div class="studyinjoy-page__seminars-inner">
        <h2 class="studyinjoy-page__section-heading">Семинары Валерии Мещеряковой</h2>

        <p class="mt-d-3 mt-2">
          Система Валерии Мещеряковой I LOVE ENGLISH - это целая технология, которую учителю важно понимать и
          придерживаться, если он хочет получить впечатляющий результат. Поэтому, прежде чем приступить к работе,
          внимательно изучить информацию, представленную на Базовом семинаре.
        </p>

        <p class="mt-1">
          На этом семинаре Валерия Мещерякова обучает тому, как обеспечить полное взаимопонимание учителя и детей в
          условиях погружения в языковую среду, психологическим и педагогическим особенностям работы с каждой возрастной
          группой, и технике работы по методическим и учебным пособиям системы. Система называется эмпирической,
          поскольку все её составляющие основаны на опыте преподавания автора.
        </p>

        <p class="mt-1">
          Последние 30 лет эта система используется огромным количеством учителей (более 2000) и обогащается их опытом,
          поэтому мы точно уверены в её результативности.
        </p>

        <div class="flex-adaptive mt-5">
          <img class="studyinjoy-page__seminars-img" :src="asset('img/study-in-joy/seminars-img.webp')" />
          <div class="mt-3 mr-3"></div>
          <div class="text-body">
            <p class="studyinjoy-page__seminars-author-name">Валерия Мещерякова</p>
            <p class="mt-1">Автор методики и системы раннего обучения языкам I LOVE ENGLISH</p>
            <p :class="{ 'mt-1': isMobile }">
              Автор пособий I Can Sing, I Can Speak, I Can Read, I Can Write, I Can Analyse и серии книг I Love Reading.
            </p>
          </div>
        </div>

        <div class="mt-5 d-flex justify-center">
          <v-button mode="warn xlarge" href="https://promo.mirgovorit.ru/basic_seminar"> Базовый семинар </v-button>
        </div>
      </div>

      <ul class="studyinjoy-page__seminars-gallery">
        <li><img :src="asset('img/study-in-joy/seminars-gallery-1.webp')" /></li>
        <li><img :src="asset('img/study-in-joy/seminars-gallery-2.webp')" /></li>
        <li><img :src="asset('img/study-in-joy/seminars-gallery-3.webp')" /></li>
        <li><img :src="asset('img/study-in-joy/seminars-gallery-4.webp')" /></li>
      </ul>
    </section>

    <section class="studyinjoy-page__books">
      <img
        class="studyinjoy-page__books-deco studyinjoy-page__books-deco_left"
        :src="asset('img/study-in-joy/books-deco-left.svg')"
      />
      <img
        class="studyinjoy-page__books-deco studyinjoy-page__books-deco_right"
        :src="asset('img/study-in-joy/books-deco-right.svg')"
      />

      <div class="studyinjoy-page__books-inner">
        <img class="studyinjoy-page__books-img" :src="asset('img/study-in-joy/books-img.webp')" />

        <div class="studyinjoy-page__books-content pa-d-5">
          <h2 class="studyinjoy-page__books-heading">Учебные и методические пособия</h2>

          <p class="mt-3">
            В нашем интернет-магазине <b>Studyinjoy</b> вы найдете авторские учебные и методические пособия Валерии
            Мещеряковой по обучению иностранным языкам детей дошкольного и младшего школьного возраста, а также книги и
            учебные пособия других авторов, которые создаются как дополнительный материал для занятий с детьми.
          </p>

          <p class="mt-2">
            Часть пособий представлена в электронном виде - <b>"Умный Учебник"</b>. Он содержит весь функционал бумажных
            пособий, плюс большое количество дополнительных возможностей: мультипликационную интерактивную версию
            аудиоуроков, кабинет аватаров и возможность выполнять домашнее задание в электронном виде.
          </p>

          <ul class="flex-adaptive studyinjoy-page__books-actions mt-3">
            <v-button mode="primary block xlarge" href="https://eng-shop.ru/" target="_blank">
              <v-icon class="mr-1">mdi-open-in-new</v-icon>
              Studyinjoy
            </v-button>

            <div class="ma-1"></div>

            <v-button mode="warn block xlarge" href="/parent/smartbook_info"> Умный Учебник </v-button>
          </ul>
        </div>
      </div>
    </section>

    <section class="studyinjoy-page__articles">
      <h2 class="studyinjoy-page__section-heading">Статьи</h2>
      <ul class="studyinjoy-page__articles-list">
        <a href="/public/study_in_joy/articles/1">
          <article class="studyinjoy-article-card">
            <img :src="asset('img/study-in-joy/articles-temp-img1.jpeg')" />
            <h3>Алгоритм работы по аудиоуроку с бумажной версией учебника</h3>
            <p>Аудиоуроки помогают детям осваивать язык через игру и повторение в увлекательной форме.</p>
          </article>
        </a>
        <a href="/public/study_in_joy/articles/2">
          <article class="studyinjoy-article-card">
            <img :src="asset('img/study-in-joy/articles-temp-img2.jpeg')" />
            <h3>Взаимопонимание в языковой среде</h3>
            <p>Как создать атмосферу полного взаимопонимания на уроках иностранного языка?</p>
          </article>
        </a>
        <a href="/public/study_in_joy/articles/3">
          <article class="studyinjoy-article-card">
            <img :src="asset('img/study-in-joy/articles-temp-img3.jpeg')" />
            <h3>Импринтинг</h3>
            <p>Как раннее изучение языка формирует прочную основу для будущего</p>
          </article>
        </a>
      </ul>

      <div class="d-flex justify-center">
        <v-button mode="xlarge outlined" href="/public/study_in_joy/articles"> Все статьи </v-button>
      </div>
    </section>

    <footer class="studyinjoy-page__footer">
      <div class="studyinjoy-page__footer-content">
        <div class="d-flex flex-column align-start">
          <img height="48" :src="asset('img/logo.svg')" />

          <template v-if="!isMobile">
            <span class="flex-spacer my-1"></span>

            <p class="text-body-bold">ИП Мещеряков О.Ю.</p>

            <p class="text-body mt-2">ОГРНИП 321169000014352</p>
            <p class="text-body mt-1">ИНН 165703420351</p>
          </template>
        </div>

        <ul class="studyinjoy-page__footer-list">
          <li class="studyinjoy-page__footer-list-title">Навигация</li>

          <li>
            <v-link href="/public/">Главная страница</v-link>
          </li>
          <li class="mt-2">
            <v-link href="https://promo.mirgovorit.ru/basic_seminar">Базовый семинар</v-link>
          </li>
          <li class="mt-2">
            <v-link href="/parent/smartbook_info">Умный Учебник</v-link>
          </li>
          <li class="mt-2">
            <v-link href="/public/study_in_joy/articles">Статьи</v-link>
          </li>
        </ul>

        <ul class="studyinjoy-page__footer-list">
          <li class="studyinjoy-page__footer-list-title">Общая информация</li>

          <li>
            <v-link :href="ctx.links.oferta" target="_blank"> Пользовательское соглашение </v-link>
          </li>
          <li class="mt-2">
            <v-link href="/help/center" target="_blank"> Справочный центр </v-link>
          </li>
        </ul>

        <ul class="studyinjoy-page__footer-list">
          <li class="studyinjoy-page__footer-list-title">Контакты</li>

          <li>
            <v-link @click="contactForm?.$open()"> Написать в поддержку </v-link>
          </li>

          <div class="d-flex mt-4">
            <v-link href="https://vk.com/ilemirgovorit" target="_blank" class="mr-2">
              <v-icon size="40">social-vk</v-icon>
            </v-link>
            <v-link href="https://t.me/ileconf2024" target="_blank" class="mr-2">
              <v-icon size="40">social-tg</v-icon>
            </v-link>
            <v-link href="https://www.youtube.com/@mirgovorit" target="_blank">
              <v-icon size="40">social-yt</v-icon>
            </v-link>
          </div>
        </ul>
      </div>

      <div v-if="isMobile" class="d-flex flex-column mt-5">
        <p class="text-body-bold">ИП Мещеряков О.Ю.</p>

        <p class="text-body mt-2">ОГРНИП 321169000014352</p>
        <p class="text-body mt-1">ИНН 165703420351</p>
      </div>

      <div class="flex-adaptive justify-end mt-5 gray-text">
        <span> &copy; Мещерякова В.Н. 2020 </span>
        <span class="mx-3 mt-1"></span>
        <span> &copy; Кирякин И.В. 2020 </span>
      </div>
    </footer>

    <ContactForm ref="contactForm" />

    <v-rich-modal v-model="teacherModalOpen">
      <template #header>
        <p class="flex-spacer">Преимущества методики для <span class="blue-text">учителей</span></p>
      </template>

      <article class="studyinjoy-page__pros-modal-content">
        <ul>
          <li>
            Все уроки и материалы расписаны на несколько лет вперёд, поэтому учитель видит перспективу и уверенно ведёт
            детей к цели.
          </li>
          <li>
            Учителя активно общаются друг с другом в закрытом чате ТГ. Поэтому у новых учителей всегда есть возможность
            посоветоваться и получить поддержку более опытных коллег.
          </li>
          <li>Платформа МирГоворит предоставляет быстрый доступ к материалам для подготовки к урокам.</li>
          <li>
            Для каждого учителя автоматически формируется индивидуальный маршрут роста, позволяющий найти оптимальный
            алгоритм развития педагогического мастерства
          </li>
          <li>
            В нашей системе созданы годичные курсы повышения квалификации - курсы Асов, на которых опытные методисты
            гарантированно выведут вас на новый уровень
          </li>
          <li>
            Дважды в год наши учителя собираются на несколько дней в педагогическом лагере, чтобы пообщаться и
            поделиться своим опытом
          </li>
        </ul>
      </article>
    </v-rich-modal>

    <v-rich-modal v-model="parentModalOpen">
      <template #header>
        <p class="flex-spacer">Преимущества методики для <span class="green-text">родителей</span></p>
      </template>

      <article class="studyinjoy-page__pros-modal-content">
        <ul>
          <li>Ребёнка не надо заставлять заниматься английским, он с радостью посещают занятия</li>
          <li>У ребёнка есть чёткая программа, по которой он движется на протяжении нескольких лет</li>
          <li>На каждой ступени можно легко отследить уровень продвижения ребёнка</li>
          <li>
            Если ребёнок занимался по нашей программой ещё до того, как начались уроки английского в школе, этот предмет
            будет даваться ему легко
          </li>
          <li>
            Поскольку наша программа основана на игровом подходе к обучению, у ребёнка, параллельно с английским, будут
            развиваться память и внимание, и он также научится игровой дисциплине
          </li>
          <li>
            В нашей системе активно используется принцип Минимакса. Он подразумевает вовлечение родителей в процесс
            обучения с учётом степени их загруженности.
          </li>
        </ul>

        <p class="mt-2 mb-1">
          <strong>Программа Минимум не требует от родителя знания языка. Всё, что требуется, это:</strong>
        </p>

        <ol>
          <li>Приобрести для ребёнка пластиковую папочку, в которой он будет хранить свои пособия. (1 раз)</li>
          <li>
            Обеспечить техническую сторону процесса - организовать рабочее место ребёнка, купить ему цветные карандаши,
            продумать, на чём он будет прослушивать Аудиоуроки. (1 раз)
          </li>
          <li>
            Ежедневно напоминать ребёнку о необходимости прослушать Аудиоурок. Сам родитель не обязан слушать его с
            ребёнком. (1 мин. в день)
          </li>
          <li>
            Раз в 2 недели (обычно учитель напоминает об этом в Мессенджере) напомнить ребёнку о необходимости выполнить
            задания к Контрольному уроку. Родитель не обязан помогать ребёнку или проверять правильность выполнения
            заданий. Это работа учителя. (1 мин. раз в 2 недели)
          </li>
          <li>
            Один раз в полгода помочь ребёнку оформить Книжку - самоделку (это результат его проектной работы) (полчаса
            1раз в полгода)
          </li>
          <li>Посещать родительские собрания и открытые уроки. (2-4 раза в год)</li>
          Обеспечить регулярность посещения занятий ребёнком.
        </ol>

        <p class="mt-2 mb-1">
          <strong>Занимаясь со своим ребёнком по программе Максимум вы можете:</strong>
        </p>

        <ol>
          <li>Играть с ребенком в различные игры из нашей методички и придумывать свои</li>
          <li>Говорить с ним по-английски дома</li>
          <li>Вместе выполнять домашние задания</li>
          <li>Помогать учителю создавать языковую среду на уроке (если учитель не против)</li>
        </ol>
      </article>
    </v-rich-modal>

    <v-rich-modal v-model="childModalOpen">
      <template #header>
        <p class="flex-spacer">Преимущества методики для <span class="orange-text">детей</span></p>
      </template>

      <article class="studyinjoy-page__pros-modal-content">
        <ul>
          <li>Дети обожают уроки, на которых они играют</li>
          <li>Большое количество игрушек и картинок привлекают внимание</li>
          <li>Высокий темп приводит к тому, что уроки пролетают незаметно</li>
          <li>Атмосфера поддержки снимает языковой барьер</li>
          <li>Подвижные игры на дают засидеться на одном месте</li>
          <li>Проектная деятельность демонстрирует, как быстро они растут и развиваются</li>
          <li>
            Иногда дети превосходят своих родителей в умении говорить на английском, и это формирует чувство уверенности
            в себе
          </li>
        </ul>
      </article>
    </v-rich-modal>
  </main>
</template>

<style lang="scss">
@use '@/styles/pages/public/studyinjoy.scss' as *;
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');
</style>
